import {
  Box,
  Grid,
  Stack,
  Toolbar,
  Typography,
  TextField,
  CircularProgress,
  Button,
  IconButton,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from '../../../utils/Api';
import { CloseOutlined, Inventory2Outlined, KeyboardArrowLeft } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const StockRequest = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const outletData = JSON.parse(localStorage.getItem('outletData') || '{}');

  const [allRawMaterials, setAllRawMaterials] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [quantities, setQuantities] = useState({});

  const [searchQuery, setSearchQuery] = useState('');

  const fetchAllRawMaterials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API.BASE_URL}raw-material`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          outletId: outletData?._id,
          limit: 1000,
        },
      });

      // Be flexible with data path
      const list =
        response?.data?.data?.rawMaterials ||
        response?.data?.data?.dbResponse ||
        response?.data?.rawMaterials ||
        [];

      setAllRawMaterials(list);
      // console.log('RAW MATERIAL API RESPONSE:', list);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch raw materials.');
    } finally {
      setLoading(false);
    }
  };

  // Helper: normalize one item whether it is plain or wrapped in { rawMaterial }
  const getRawMeta = (item) => {
    const raw = item?.rawMaterial || item || {};
    return {
      id: raw._id,
      name: raw.name || '',
      unit: raw.unit || '',
      mainCategoryName: raw.mainCategory?.name || '',
    };
  };

  useEffect(() => {
    fetchAllRawMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter based on name (support both shapes)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setRawMaterials([]);
      return;
    }

    const q = searchQuery.toLowerCase();

    const filtered = allRawMaterials.filter((item) => {
      const { name } = getRawMeta(item);
      return name.toLowerCase().includes(q);
    });

    setRawMaterials(filtered);
  }, [allRawMaterials, searchQuery]);

  const handleQuantityChange = (itemId, inputValue) => {
    const numericValue = parseFloat(inputValue);

    setQuantities((prev) => {
      const updated = { ...prev };

      if (!inputValue || isNaN(numericValue) || numericValue <= 0) {
        delete updated[itemId];
      } else {
        updated[itemId] = inputValue;
      }

      return updated;
    });

    setSelectedItems((prevSelected) => {
      if (!isNaN(numericValue) && numericValue > 0) {
        if (!prevSelected.includes(itemId)) {
          return [...prevSelected, itemId];
        }
        return prevSelected;
      }
      return prevSelected.filter((id) => id !== itemId);
    });
  };

  const handleSubmit = async () => {
    if (!selectedItems.length) {
      toast.error('Please select at least one item by adding quantities.');
      return;
    }

    const items = selectedItems.map((itemId) => ({
      rawMaterial: itemId,
      requestedQuantity: Number(quantities[itemId]),
    }));

    try {
      setLoading(true);

      const response = await axios.post(
        `${API.BASE_URL}material-request-indent`,
        {
          outlet: outletData?._id,
          items,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response?.data) throw new Error('Submission failed.');
      toast.success('Stock request sent successfully.');

      setSelectedItems([]);
      setQuantities({});
      fetchAllRawMaterials();
      navigate('/stock-requistion-requests');
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.data?.title || error?.response?.data?.error || 'Failed to send stock request.'
      );
    } finally {
      setLoading(false);
    }
  };

  const hasSelection = selectedItems.length > 0;

  return (
    <Box sx={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Toolbar />
      <Box
        sx={{
          maxWidth: 1300,
          mx: 'auto',
          px: 3,
          pb: 4,
        }}
      >
        {/* Page header */}
        <Box sx={{ mt: 5, mb: 2, pt: 2, display: 'flex' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <KeyboardArrowLeft onClick={() => navigate(-1)} fontSize='large' sx={{ cursor: 'pointer' }} />
            <Box
              sx={(theme) => ({
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.primary.light + '22',
                color: theme.palette.primary.main,
              })}
            >
              <Inventory2Outlined fontSize="medium" />
            </Box>
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color="#003049">
              Stock Requisition
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search for raw materials and specify required quantities to raise a
              stock request for your outlet.
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={2}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            mt: 2,
          }}
        >
          <Grid container>
            {/* LEFT: Search + Results list */}
            <Grid
              item
              md={6}
              xs={12}
              sx={{
                borderRight: { md: '1px solid #e0e0e0', xs: 'none' },
                backgroundColor: '#fafbfc',
              }}
            >
              {/* Search bar */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: '#fafbfc',
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Search raw materials by name"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Start typing to search. Results will appear below.
                </Typography>
              </Box>

              {/* List area */}
              <Box sx={{ p: 2, height: '70vh', overflowY: 'auto' }}>
                {loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Fetching raw materials...
                    </Typography>
                  </Box>
                ) : !searchQuery.trim() ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      Start by searching above.
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      Type at least one character to see matching raw materials.
                    </Typography>
                  </Box>
                ) : rawMaterials?.length > 0 ? (
                  <Grid container spacing={1.5}>
                    {rawMaterials.map((item) => {
                      const meta = getRawMeta(item);
                      const { id, name, unit, mainCategoryName } =
                        meta;

                      if (!id) return null;

                      const isSelected = selectedItems.includes(id);

                      return (
                        <Grid item xs={12} key={id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: isSelected
                                ? '2px solid #003049'
                                : '1px solid #e0e0e0',
                              backgroundColor: isSelected
                                ? '#f0f6fb'
                                : '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                boxShadow: 2,
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            <Box sx={{ flex: 1, pr: 2 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{ textTransform: 'capitalize' }}
                              >
                                {name}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                mt={0.5}
                              >
                                {!!mainCategoryName && (
                                  <Chip
                                    size="small"
                                    label={mainCategoryName}
                                    sx={{ fontSize: 11, height: 22 }}
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </Box>
                            <TextField
                              variant="outlined"
                              placeholder="Qty"
                              value={quantities[id] || ''}
                              onChange={(e) =>
                                handleQuantityChange(id, e.target.value)
                              }
                              onKeyDown={(e) => {
                                const allowedControlKeys = [
                                  'Backspace',
                                  'Tab',
                                  'ArrowLeft',
                                  'ArrowRight',
                                  'Delete',
                                  'Home',
                                  'End',
                                ];
                                if (allowedControlKeys.includes(e.key)) return;
                                if (!/[0-9.]/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              size="small"
                              sx={{
                                width: 120,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                },
                                '& input': {
                                  textAlign: 'right',
                                },
                              }}
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      No raw materials found.
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      No results for &quot;{searchQuery}&quot;. Try another term.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* RIGHT: Selected items & submit */}
            <Grid
              item
              md={6}
              xs={12}
              sx={{
                backgroundColor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Selected Items
                  </Typography>
                  <Chip
                    size="small"
                    label={`${selectedItems.length} selected`}
                    color={hasSelection ? 'primary' : 'default'}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                </Box>

                <Divider />

                <Box
                  sx={{
                    flex: 1,
                    mt: 1.5,
                    overflowY: 'auto',
                    maxHeight: '55vh',
                  }}
                >
                  {hasSelection ? (
                    <Stack spacing={1.2} alignItems={'center'}>
                      {selectedItems.map((itemId) => {
                        const wrapper = allRawMaterials.find((i) => {
                          const meta = getRawMeta(i);
                          return meta.id === itemId;
                        });

                        if (!wrapper) return null;

                        const meta = getRawMeta(wrapper);
                        const { name, unit } = meta;

                        return (
                          <Box
                            key={itemId}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 1.5,
                              py: 1,
                              background:
                                'linear-gradient(135deg, #003049, #045182)',
                              borderRadius: 2,
                              color: '#fff',
                              width: '100%',
                              maxWidth: 420,
                            }}
                          >
                            <Box sx={{ flex: 1, pr: 1 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={500}
                                sx={{
                                  textTransform: 'capitalize',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ opacity: 0.85 }}
                              >
                                {quantities[itemId]} {unit}
                              </Typography>
                            </Box>
                            <IconButton
                              onClick={() => {
                                setSelectedItems((prev) =>
                                  prev.filter((id) => id !== itemId)
                                );
                                setQuantities((prev) => {
                                  const updated = { ...prev };
                                  delete updated[itemId];
                                  return updated;
                                });
                              }}
                              sx={{
                                color: '#fff',
                                '&:hover': {
                                  color: '#e0e0e0',
                                  backgroundColor: 'rgba(255,255,255,0.08)',
                                },
                              }}
                              size="small"
                            >
                              <CloseOutlined fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        No items selected yet.
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 0.5 }}>
                        Search and add quantities from the left panel to include
                        items in this request.
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!hasSelection || loading}
                    sx={{
                      px: 8,
                      py: 1.2,
                      borderRadius: 999,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      backgroundColor: '#003049',
                      '&:hover': {
                        backgroundColor: '#002433',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#c5c9cc',
                        color: '#ffffff',
                      },
                    }}
                  >
                    {loading ? 'Processing...' : 'Send Stock Request'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default StockRequest;
